import {PhoneHQButton, PhoneHQModal, PhoneHQSelect, Segmented} from "ch3-ui-lib";
import {FileAudio, Mic, Play, Square, Trash2, Upload} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {WaveFile} from "wavefile";
import AudioPlayer from "../AudioPlayer/AudioPlayer";

/** What a text-to-speech message was made from; kept so "Change" reopens with it. */
export interface TtsSettings {
    text: string;
    gender: string;
    language: string;
}

type Mode = 'tts' | 'record' | 'upload';

const TABS: { id: Mode; label: string }[] = [
    {id: 'tts', label: 'Text to speech'},
    {id: 'record', label: 'Record'},
    {id: 'upload', label: 'Upload'},
];

const LANGUAGES = ['Chinese', 'Czech', 'English', 'French', 'German', 'Italian', 'Japanese', 'Polish', 'Spanish']
    .map((l) => ({value: l, label: l}));
const VOICES = [
    {value: 'Female', label: 'Female voice'},
    {value: 'Male', label: 'Male voice'},
    {value: 'Neutral', label: 'Neutral voice'},
];

export const TEXT_MAX = 500;
const RECORD_MAX_SECONDS = 60;
const UPLOAD_MAX_BYTES = 16 * 1024 * 1024;
const UPLOAD_TYPES = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3'];

type Upload = (args: { id: string; data: FormData; audioURL: string }) => unknown;
type Synthetize = (args: { text: string; language: string; gender: string }) => unknown;
type UploadSynth = (args: { id: string; text: string; language: string; gender: string }) => unknown;

interface MessageDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** "Greeting message", "Menu message", "Voicemail greeting"… */
    title: string;
    /** Label of the text field, defaults to "Message". */
    textLabel?: string;
    /** The message saved now, played under Text to speech until a new preview exists. */
    audioFile?: string;
    savedTts?: TtsSettings;
    setSavedTts: (tts: TtsSettings) => void;
    /** Uploads a recorded or chosen file; the owner reads the result from its mutation. */
    uploadFile: Upload;
    /** Makes a preview of the text (the owner keeps the preview URL in its mutation). */
    synthetizeText: Synthetize;
    /** Stores the text-to-speech message. */
    uploadSynthText: UploadSynth;
    /** Called after a successful save, so the owner attaches the new file. */
    onSaved: () => void;
}

/**
 * Message editor dialog (Queues-GreetingDialog / -MenuMessageDialog, SettingsPhone-Welcome / -WelcomeRecord / -WelcomeUpload):
 * Text to speech, Record or Upload, saved with one Save in the footer.
 */
export const MessageDialog = ({open, onOpenChange, title, textLabel = 'Message', audioFile, savedTts, setSavedTts, uploadFile, synthetizeText, uploadSynthText, onSaved}: MessageDialogProps) => {
    const {id = ''} = useParams<{ id: string }>();
    const [mode, setMode] = useState<Mode>('tts');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Text to speech
    const [text, setText] = useState('');
    const [language, setLanguage] = useState('English');
    const [voice, setVoice] = useState('Female');
    const [preview, setPreview] = useState<{ url: string; key: string } | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const ttsKey = `${text}|${language}|${voice}`;

    // Record / upload
    const [clip, setClip] = useState<{ blob: Blob; url: string; name: string } | null>(null);

    useEffect(() => {
        if (!open) return;
        setMode('tts');
        setText(savedTts?.text ?? '');
        setLanguage(savedTts?.language ? capitalize(savedTts.language) : 'English');
        setVoice(savedTts?.gender ? capitalize(savedTts.gender) : 'Female');
        setPreview(null);
        setClip(null);
        setError(null);
        setSaving(false);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const switchMode = (next: Mode) => {
        setMode(next);
        setClip(null);
        setError(null);
    };

    const ttsArgs = {text: text.trim(), language: language.toUpperCase(), gender: voice.toUpperCase()};

    const playPreview = async () => {
        setPreviewing(true);
        setError(null);
        try {
            const res = await synthetizeText(ttsArgs) as { data?: { data?: string } };
            if (res?.data?.data) setPreview({url: res.data.data, key: ttsKey});
            else setError('Couldn’t make a preview. Try again.');
        } finally {
            setPreviewing(false);
        }
    };

    const canSave = !saving && (mode === 'tts' ? Boolean(text.trim()) : Boolean(clip));

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            if (mode === 'tts') {
                // The owner plays the saved message from the preview, so make one if the text changed.
                if (preview?.key !== ttsKey) check(await synthetizeText(ttsArgs));
                check(await uploadSynthText({id, ...ttsArgs}));
                setSavedTts({text: ttsArgs.text, language, gender: voice});
            } else if (clip) {
                const data = new FormData();
                data.append('messageFile', clip.blob, clip.name);
                data.append('fileName', clip.name);
                check(await uploadFile({id, data, audioURL: clip.url}));
            }
            onSaved();
            onOpenChange(false);
        } catch {
            setError('Couldn’t save the message. Try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <PhoneHQModal
            open={open}
            onOpenChange={onOpenChange}
            title={title}
            width={520}
            footer={
                <>
                    <PhoneHQButton variant="secondary" className="px-5" onClick={() => onOpenChange(false)}>Cancel</PhoneHQButton>
                    <PhoneHQButton className="px-5" disabled={!canSave} onClick={save}>{saving ? 'Saving…' : 'Save'}</PhoneHQButton>
                </>
            }
        >
            <div className="flex flex-col gap-3.5">
                <Segmented label="How to create the message" options={TABS} value={mode} onChange={switchMode} fullWidth/>

                {mode === 'tts' && (
                    <>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="message-text" className="text-footnote font-medium text-ink-secondary">{textLabel}</label>
                            <textarea
                                id="message-text"
                                value={text}
                                maxLength={TEXT_MAX}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="What callers hear, e.g. Thank you for calling. Please hold and we will connect you shortly."
                                className="box-border min-h-28 w-full resize-y rounded-control border border-solid border-fill-subtle bg-surface px-3 py-2.5 font-[inherit] text-callout leading-[22px] text-ink outline-none placeholder:text-ink-muted focus:border-transparent focus:ring-2 focus:ring-brand-focus"
                            />
                            <span className="text-right text-footnote text-ink-muted">{text.length}/{TEXT_MAX}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <PhoneHQSelect label="Language" value={language} onChange={setLanguage} options={LANGUAGES}/>
                            <PhoneHQSelect label="Voice" value={voice} onChange={setVoice} options={VOICES}/>
                        </div>
                        <PhoneHQButton variant="secondary" className="w-full" disabled={!text.trim() || previewing} onClick={playPreview}>
                            <Play size={16} aria-hidden="true"/>
                            {previewing ? 'Making preview…' : 'Play preview'}
                        </PhoneHQButton>
                        {(preview?.url || audioFile) && <AudioPlayer src={preview?.url ?? audioFile!}/>}
                    </>
                )}

                {mode === 'record' && <Recorder clip={clip} onClip={setClip} onError={setError}/>}
                {mode === 'upload' && <FilePicker clip={clip} onClip={setClip} onError={setError}/>}

                {error && <p role="alert" className="m-0 text-footnote text-danger-ink">{error}</p>}
            </div>
        </PhoneHQModal>
    );
};

/** RTK mutation triggers resolve with `{error}` instead of throwing. */
const check = (result: unknown) => {
    if (result && typeof result === 'object' && 'error' in result && (result as { error?: unknown }).error) throw (result as { error: unknown }).error;
    return result;
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

type Clip = { blob: Blob; url: string; name: string };

/** SettingsPhone-WelcomeRecord: one big button, timer, up to 60 seconds, saved as WAV. */
const Recorder = ({clip, onClip, onError}: { clip: Clip | null; onClip: (c: Clip | null) => void; onError: (e: string | null) => void }) => {
    const [recording, setRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<number | null>(null);

    useEffect(() => () => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    }, []);

    const stop = () => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
        recorderRef.current?.stop();
        setRecording(false);
    };

    const start = async () => {
        onError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            const recorder = new MediaRecorder(stream, {mimeType: 'audio/webm'});
            chunksRef.current = [];
            recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const wav = await webmToWav(new Blob(chunksRef.current, {type: 'audio/webm'}));
                onClip({blob: wav, url: URL.createObjectURL(wav), name: 'welcome_message.wav'});
            };
            recorderRef.current = recorder;
            onClip(null);
            setSeconds(0);
            recorder.start();
            setRecording(true);
            timerRef.current = window.setInterval(() => {
                setSeconds((s) => {
                    if (s + 1 >= RECORD_MAX_SECONDS) stop();
                    return s + 1;
                });
            }, 1000);
        } catch {
            onError('Allow microphone access in your browser to record.');
        }
    };

    return (
        <div className="flex flex-col items-center gap-3.5 rounded-card bg-surface-muted px-4 py-7">
            <button
                type="button"
                aria-label={recording ? 'Stop recording' : 'Start recording'}
                onClick={recording ? stop : start}
                className="flex size-[72px] cursor-pointer items-center justify-center rounded-full border-0 bg-danger p-0 text-white hover:bg-danger-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2"
            >
                {recording ? <Square size={24} aria-hidden="true" fill="currentColor"/> : <Mic size={28} aria-hidden="true"/>}
            </button>
            <span className="text-[24px] font-semibold tabular-nums text-ink" aria-live="polite">{formatSeconds(seconds)}</span>
            <span className="text-footnote text-ink-muted">
                {recording ? 'Recording… Tap to stop.' : clip ? 'Listen back, or tap to record again.' : `Tap to start recording. Up to ${RECORD_MAX_SECONDS} seconds.`}
            </span>
            {clip && !recording && <div className="w-full"><AudioPlayer src={clip.url}/></div>}
        </div>
    );
};

/** SettingsPhone-WelcomeUpload: dashed drop area with Choose file. */
const FilePicker = ({clip, onClip, onError}: { clip: Clip | null; onClip: (c: Clip | null) => void; onError: (e: string | null) => void }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    const take = (file?: File) => {
        if (!file) return;
        if (!UPLOAD_TYPES.includes(file.type)) return onError('Choose an MP3 or WAV file.');
        if (file.size > UPLOAD_MAX_BYTES) return onError('The file is larger than 16 MB.');
        onError(null);
        onClip({blob: file, url: URL.createObjectURL(file), name: file.name});
    };

    if (clip) {
        return (
            <div className="flex flex-col gap-3 rounded-card border border-solid border-line p-4">
                <div className="flex items-center gap-3">
                    <FileAudio size={20} aria-hidden="true" className="shrink-0 text-ink-muted"/>
                    <span className="min-w-0 flex-1 truncate text-callout text-ink">{clip.name}</span>
                    <button type="button" aria-label="Remove file" onClick={() => onClip(null)}
                            className="flex size-8 cursor-pointer items-center justify-center rounded-chip border-0 bg-transparent p-0 text-ink-muted hover:bg-surface-muted">
                        <Trash2 size={16} aria-hidden="true"/>
                    </button>
                </div>
                <AudioPlayer src={clip.url}/>
            </div>
        );
    }

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files?.[0]); }}
            className={`flex flex-col items-center gap-2.5 rounded-card border border-dashed bg-surface px-4 py-7 text-center ${dragging ? 'border-brand' : 'border-fill-subtle'}`}
        >
            <Upload size={22} aria-hidden="true" className="text-ink-muted"/>
            <span className="text-[15px] font-semibold text-ink">Upload an audio file</span>
            <span className="text-footnote text-ink-muted">MP3 or WAV, up to 16 MB. Drop it here or choose a file.</span>
            <PhoneHQButton variant="secondary" className="px-5" onClick={() => inputRef.current?.click()}>Choose file</PhoneHQButton>
            <input ref={inputRef} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" className="hidden" onChange={(e) => take(e.target.files?.[0])}/>
        </div>
    );
};

const formatSeconds = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

/** Browsers record WebM; the phone system plays WAV (mono, 16-bit). */
const webmToWav = (blob: Blob): Promise<Blob> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        new Ctx().decodeAudioData(reader.result as ArrayBuffer, (buffer) => {
            const samples = buffer.getChannelData(0);
            const pcm = new Int16Array(samples.length);
            for (let i = 0; i < samples.length; i++) pcm[i] = samples[i] * 0x7FFF;
            const wav = new WaveFile();
            wav.fromScratch(1, buffer.sampleRate, '16', pcm);
            resolve(new Blob([wav.toBuffer()], {type: 'audio/wav'}));
        }, reject);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
});
